import { useCallback } from 'react';
import { getNumberSeparators } from '@sas/nova-core/i18n/formatting';
import { toLatinNumberingSystemLocale } from '@sas/nova-core/i18n/localeUtils';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting';
import bundle from './NovaComponents-Input-gui-icu.properties';
const useInputLocalizer = createUseTextLocalizer(bundle);
export const combineValidators = (...validators) => {
  const validator = (value, editing, editValue) => {
    let currentEditValue = editValue;
    const overallResult = {
      message: null,
      status: null,
      replaceWith: undefined
    };
    for (const validator of validators) {
      const result = validator(value, editing, currentEditValue);
      overallResult.message = overallResult.message ?? result.message;
      overallResult.status = overallResult.status ?? result.status;
      if (typeof result.replaceWith === 'string') {
        overallResult.replaceWith = result.replaceWith;
        currentEditValue = result.replaceWith;
      }
    }
    return overallResult;
  };
  validator.validationProps = validators.map(v => ({
    ...v.validationProps
  })).reduce((previous, current) => {
    return {
      ...previous,
      ...current
    };
  }, {});
  return validator;
};

// Create error names separate from message keys. If we use message keys directly here then those message keys become
// public API. This does still have a problem that message parameters effectively become public API though.
const messages = {
  value_is_required: 'required.error.msg',
  minimum_length: 'minlength.error.msg',
  must_be_an_integer: 'numericValidator.integers.error.msg',
  must_be_a_number: 'numericValidator.numeric.error.msg',
  value_is_greater_than_inclusive_max: 'rangeValidator.maxRangeInclusive.msg',
  value_is_greater_than_exclusive_max: 'rangeValidator.maxRangeExclusive.msg',
  value_is_less_than_inclusive_min: 'rangeValidator.minRangeInclusive.msg',
  value_is_less_than_exclusive_min: 'rangeValidator.minRangeExclusive.msg',
  incorrect_telephone_format: 'telephoneValidator.msg'
};
export const useInputValidationMessages = () => {
  const localizer = useInputLocalizer();
  return useCallback(message => {
    if ('params' in message) {
      return localizer(messages[message.error], message.params);
    }
    return localizer(messages[message.error]);
  }, [localizer]);
};
const noError = () => ({
  message: null,
  status: null
});
const useErrorEarcon = () => useEarcon({
  source: EarconName.Error
});
export const useValidateRequiredValue = () => {
  const validationMessages = useInputValidationMessages();
  const earcon = useErrorEarcon();
  const validator = useCallback((value, editing) => {
    if (value === null || value === undefined || value === '') {
      if (editing) {
        earcon.play();
      }
      return {
        message: validationMessages({
          error: 'value_is_required'
        }),
        status: 'alert'
      };
    }
    return noError();
  }, [earcon, validationMessages]);
  validator.validationProps = {
    required: true
  };
  return validator;
};
export const useValidateMinimumLength = minLength => {
  const validationMessages = useInputValidationMessages();
  return useCallback(value => {
    if (value === null || value.length < minLength) {
      return {
        message: validationMessages({
          error: 'minimum_length',
          params: {
            minlength: `${minLength}`
          }
        }),
        status: 'alert'
      };
    }
    return noError();
  }, [minLength, validationMessages]);
};

/**
 * Creates a validator for integers. Parsers used with this should consider values like '123d' as Number.NaN.
 */
export const useValidateIsInteger = props => {
  const validationMessages = useInputValidationMessages();
  const formatLocale = useFormatLocale();
  const {
    groupingSeparator
  } = {
    ...getNumberSeparators(toLatinNumberingSystemLocale(formatLocale)),
    ...props?.separators
  };
  const validator = useCallback((value, editing, editValue) => {
    if (value === null) {
      return noError();
    }
    if (Number.isNaN(value) || Math.round(value) !== value) {
      if (editing && editValue === '+') {
        return noError();
      }
      const result = {
        message: validationMessages({
          error: 'must_be_an_integer'
        }),
        status: 'alert'
      };
      if (editing && props?.preventInvalidCharacters) {
        const startsWithPlus = editValue[0] === '+';
        result.replaceWith = (startsWithPlus ? '+' : '') + editValue.replace(new RegExp(`[^\\d${groupingSeparator}-]`, 'g'), '');
      }
      return result;
    }
    return noError();
  }, [groupingSeparator, props?.preventInvalidCharacters, validationMessages]);
  validator.validationProps = {
    type: 'number'
  };
  return validator;
};

/**
 * Creates a validator for numbers. Parsers used with this should consider values like '123d' as Number.NaN.
 */
export const useValidateIsNumber = props => {
  const validationMessages = useInputValidationMessages();
  const formatLocale = useFormatLocale();
  const {
    decimalSeparator,
    groupingSeparator
  } = {
    ...getNumberSeparators(toLatinNumberingSystemLocale(formatLocale)),
    ...props?.separators
  };
  const validator = useCallback((value, editing, editValue) => {
    if (editing && props?.preventInvalidCharacters) {
      if (Number.isNaN(value)) {
        // Allows users to start typing a positive number and not show an error.
        if (editValue === '+') return noError();
        const startsWithPlus = editValue[0] === '+';
        const replaceWith = (startsWithPlus ? '+' : '') + editValue.replace(new RegExp(`[^\\d${decimalSeparator}${groupingSeparator}-]`, 'g'), '');
        return {
          message: validationMessages({
            error: 'must_be_a_number'
          }),
          status: 'alert',
          replaceWith
        };
      }
    }
    if (Number.isNaN(value) || value === undefined) {
      return {
        message: validationMessages({
          error: 'must_be_a_number'
        }),
        status: 'alert'
      };
    }
    return noError();
  }, [decimalSeparator, groupingSeparator, props?.preventInvalidCharacters, validationMessages]);
  validator.validationProps = {
    type: 'number'
  };
  return validator;
};

// The min and max validators consider null an error. The implication of a minimum or maximum number implies the
// the number is needed and should not be null.
export const useValidateMinimumNumber = (min, options) => {
  const validationMessages = useInputValidationMessages();
  const formatLocale = useFormatLocale();
  const defaultDisplayFormatter = new Intl.NumberFormat(toLatinNumberingSystemLocale(formatLocale), {
    style: 'decimal',
    useGrouping: true
  });
  const {
    inclusive,
    displayMinValue
  } = {
    inclusive: true,
    displayMinValue: defaultDisplayFormatter.format(min),
    ...options
  };
  return useCallback(value => {
    if (value === null) return noError();
    if (inclusive && (Number.isNaN(value) || value < min)) {
      const message = validationMessages({
        error: 'value_is_less_than_inclusive_min',
        params: {
          minValue: displayMinValue
        }
      });
      return {
        message,
        status: 'alert'
      };
    }
    if (!inclusive && (Number.isNaN(value) || value <= min)) {
      const message = validationMessages({
        error: 'value_is_less_than_exclusive_min',
        params: {
          minValue: displayMinValue
        }
      });
      return {
        message,
        status: 'alert'
      };
    }
    return noError();
  }, [validationMessages, min, inclusive, displayMinValue]);
};
export const useValidateMaximumNumber = (max, options) => {
  const validationMessages = useInputValidationMessages();
  const formatLocale = useFormatLocale();
  const defaultDisplayFormatter = new Intl.NumberFormat(toLatinNumberingSystemLocale(formatLocale), {
    style: 'decimal',
    useGrouping: true
  });
  const {
    inclusive,
    displayMaxValue
  } = {
    inclusive: true,
    displayMaxValue: defaultDisplayFormatter.format(max),
    ...options
  };
  return useCallback(value => {
    if (value === null) return noError();
    if (inclusive && (Number.isNaN(value) || value > max)) {
      const message = validationMessages({
        error: 'value_is_greater_than_inclusive_max',
        params: {
          maxValue: displayMaxValue
        }
      });
      return {
        message,
        status: 'alert'
      };
    }
    if (!inclusive && (Number.isNaN(value) || value >= max)) {
      const message = validationMessages({
        error: 'value_is_greater_than_exclusive_max',
        params: {
          maxValue: displayMaxValue
        }
      });
      return {
        message,
        status: 'alert'
      };
    }
    return noError();
  }, [validationMessages, max, inclusive, displayMaxValue]);
};

/**
 * Creates a validator for checking the maximum number of decimal places. Use with `useValidateIsNumber` for complete
 * validation.
 *
 * @param maxDecimalPlaces The maximum number of decimal places.
 * @param decimalSeparator The decimal separator for the expected number format. Optional. Typically only include if
 * the expected decimal separator is different than the user's format locale.
 * @returns
 */
export const useValidateMaxDecimalPlaces = (maxDecimalPlaces, options) => {
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const formatLocale = useFormatLocale();
  const {
    decimalSeparator
  } = {
    ...getNumberSeparators(toLatinNumberingSystemLocale(formatLocale)),
    ...options?.separators
  };
  // const ds = decimalSeparator ?? defaultDecimalSepator;
  return useCallback((value, editing, editValue) => {
    // NaN and null don't have decimal places so it's not beyond the max.
    if (Number.isNaN(value) || value === null) {
      return noError();
    }
    const [_, afterDecimal] = String(editValue).split(decimalSeparator);
    if (afterDecimal && afterDecimal.length > maxDecimalPlaces) {
      if (editing) {
        earcon.play();
      }
      let replaceWith = undefined;
      if (editing) {
        const [beforeDecimal, editAfterDecimal] = editValue.split(decimalSeparator);
        replaceWith = beforeDecimal;
        if (editAfterDecimal) {
          replaceWith += decimalSeparator + editAfterDecimal.substring(0, maxDecimalPlaces);
        }
      }

      // No defined message for max decimal places, current behavior seems to be just correct it to the proper number
      // of digits.
      return {
        message: null,
        status: null,
        replaceWith
      };
    }
    return noError();
  }, [decimalSeparator, earcon, maxDecimalPlaces]);
};
export const useValidationMaxLength = maxLength => {
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  return useCallback((value, editing) => {
    if (value === null) {
      return noError();
    }

    // No defined message for max length, current behavior seems to be just correct it down to the proper length.
    if (value.length > maxLength) {
      if (editing) {
        earcon.play();
      }
      return {
        message: null,
        status: null,
        replaceWith: value.substring(0, maxLength)
      };
    }
    return noError();
  }, [earcon, maxLength]);
};
export const useValidateTelephone = () => {
  const validationMessages = useInputValidationMessages();
  return useCallback((value, editing, editValue) => {
    if (value === null || value === '') {
      return noError();
    }
    const errorResult = {
      message: validationMessages({
        error: 'incorrect_telephone_format'
      }),
      status: 'alert'
    };
    if (editing) {
      const editRegex = /^[\d|\+\d{1,3}\s|\(]+[\)|\d|\s|\-|\.]{0,}$/;
      if (editRegex.test(editValue)) {
        return noError();
      }
      return errorResult;
    }
    const telephoneRegex = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
    if (value && telephoneRegex.test(value)) {
      return noError();
    }
    return errorResult;
  }, [validationMessages]);
};
//# sourceMappingURL=validation.js.map